import { useEffect, useState } from 'react';
import { facilitySchedule, facilityScheduleSave, type ApiError, type ScheduleEntry } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { ErrLine, I, btn, chip, pre, sx, useApi, useGuarded, usePage } from './kit';
import { BuildingLoading, PickOneBuilding, useBuilding } from './building';

interface Item { kind: 'MEAL' | 'ACTIVITY'; name: string; time: string; days?: number[] }
const INPUT = 'width:100%;height:36px;padding:0 12px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#F7F3EA;font-size:16px;color:#22272E;font-family:inherit;box-sizing:border-box;height:32px;';
const fromEntry = (e: ScheduleEntry): Item => ({ kind: e.kind === 'meal' ? 'MEAL' : 'ACTIVITY', name: e.label, time: e.start_time, days: e.days_of_week });
const same = (a: Item[], b: Item[]) => JSON.stringify(a.map((x) => [x.kind, x.name, x.time])) === JSON.stringify(b.map((x) => [x.kind, x.name, x.time]));

/**
 * Daily Schedule (design seIsSchedule, T9484; ds* L22309-22326): the building's posted times.
 * A Facility Admin's own building; Super Admin and Operator name the building they picked in
 * the facility switcher, and are asked to pick one when they have not (a building section).
 */
export function ScheduleSection() {
  const building = useBuilding();
  if (building.mode === 'loading') return <BuildingLoading />;
  if (building.mode === 'own') return <BuildingSchedule key="own" orgId={null} name={null} />;
  if (building.mode === 'picked') return <BuildingSchedule key={building.orgId} orgId={building.orgId} name={building.name} />;
  return <PickOneBuilding what="Daily Schedule" />;
}

/** One building's schedule: the session's own (orgId null), or the one Aithora named. */
function BuildingSchedule({ orgId, name }: { orgId: string | null; name: string | null }) {
  const { client } = useSession();
  const { ask, changed } = usePage();
  const { canWrite, roReason, wr } = useGuarded();
  const q = useApi((c, s) => facilitySchedule(c, { query: orgId ? { organizationId: orgId } : {}, signal: s }), [orgId]);
  const server = (q.data?.schedule ?? []).map(fromEntry);
  const [items, setItems] = useState<Item[] | null>(null);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  useEffect(() => {
    if (q.data && items === null) setItems((q.data.schedule ?? []).map(fromEntry));
  }, [q.data, items]);
  if (!q.data && !q.error) return <Skeleton rows={4} label="Loading the schedule" />;
  const list = items ?? server;
  const dirty = !same(list, server);
  const unsaved = Math.max(list.filter((x) => !server.some((s) => s.kind === x.kind && s.name === x.name && s.time === x.time)).length, dirty ? 1 : 0);
  const edit = (i: number, patch: Partial<Item>) => {
    setItems(list.map((x, j) => (j === i ? { ...x, ...patch } : x)));
    setSaved(false);
  };
  const save = async () => {
    if (!dirty || !canWrite) return;
    setErr(null);
    try {
      await facilityScheduleSave(client, {
        body: { ...(orgId ? { organizationId: orgId } : {}), entries: list.map((x) => ({ kind: x.kind === 'MEAL' ? 'meal' : 'activity', label: x.name.trim(), startTime: x.time.trim(), ...(x.days ? { daysOfWeek: x.days } : {}) })) },
      });
      setSaved(true);
      setItems(null);
      q.reload();
      changed();
    } catch (e) {
      setErr(e as ApiError);
    }
  };

  return (
    <div data-testid="settings-schedule" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:16px 18px;max-width:820px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:4px;')}>
        <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;')} />
        <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Daily schedule</span>
      </div>
      <div style={sx('font-size:16px;color:#54606C;margin-bottom:15px;')}>{I((name ? name + ': the' : 'The') + ' posted meal and activity times Atlas reads back to residents. Set them in facility-local time.')}</div>
      {q.error && <ErrLine error={q.error} style="margin:0 0 15px;" />}
      {list.length === 0 && <div style={sx('font-size:16px;color:#54606C;margin-bottom:15px;')}>No schedule yet. Add a meal or activity below.</div>}
      {list.map((r, i) => (
        <div key={i} style={sx('display:grid;grid-template-columns:96px minmax(0,1fr) 110px 90px;gap:11px;align-items:center;padding:8px 0;border-top:1px solid rgba(34,39,46,.07);')}>
          <span style={sx(chip(r.kind === 'MEAL' ? '#5B6B7C' : '#264C7E', r.kind === 'MEAL' ? 'rgba(91,107,124,.18)' : 'rgba(79,138,214,.16)') + 'text-align:center;')}>{I(r.kind)}</span>
          <input type="text" aria-label="What it is" value={r.name} readOnly={!canWrite} onChange={(e) => edit(i, { name: e.target.value })} placeholder="what it is" style={sx(INPUT)} />
          <input type="text" aria-label="Time" value={r.time} readOnly={!canWrite} onChange={(e) => edit(i, { time: e.target.value })} placeholder="07:30" style={sx(INPUT + 'text-align:center;')} />
          <a
            href="#"
            className="se-hover-dark"
            {...wr(() => ask({
              sec: 'Daily Schedule', title: 'Remove ' + (r.name.trim() || 'this item') + '?',
              body: 'Residents and staff stop seeing it on the daily schedule from the next refresh.', btn: 'Remove',
              go: async () => {
                setItems(list.filter((_, j) => j !== i));
                setSaved(false);
              },
            }))}
            style={sx('font-size:16px;color:#B3261E;text-decoration:none;text-align:right;')}
          >
            Remove
          </a>
        </div>
      ))}
      <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-top:15px;flex-wrap:wrap;row-gap:9px;')}>
        <a href="#" {...wr(() => { setItems([...list, { kind: 'MEAL', name: '', time: '' }]); setSaved(false); })} style={sx(btn('ghost') + 'height:26px;')}>+ Add meal</a>
        <a href="#" {...wr(() => { setItems([...list, { kind: 'ACTIVITY', name: '', time: '' }]); setSaved(false); })} style={sx(btn('ghost') + 'height:26px;')}>+ Add activity</a>
        <span style={sx('flex:1;')} />
        <span style={sx('font-size:16px;' + (dirty ? 'color:#5B6B7C;font-weight:600;' : 'color:#54606C;'))}>
          {I(dirty ? unsaved + (unsaved === 1 ? ' entry unsaved' : ' entries unsaved') : saved ? 'Schedule saved' : 'Nothing to save')}
        </span>
        <a
          href="#"
          role="button"
          aria-disabled={!dirty || !canWrite || undefined}
          title={roReason ?? undefined}
          onClick={(e) => { pre(e); void save(); }}
          style={sx('display:inline-flex;align-items:center;height:26px;padding:0 16px;border-radius:9px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' + (dirty ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#54606C;border:1px solid rgba(34,39,46,.2);'))}
        >
          Save schedule
        </a>
      </div>
      <ErrLine error={err} />
      <div style={sx('font-size:16px;color:#54606C;margin-top:12px;line-height:1.55;')}>
        {I('Atlas reads these back when a resident asks what is for lunch or when bingo starts, so a wrong time here becomes a wrong answer at the bedside.')}
      </div>
    </div>
  );
}
